import { fetchAllPhones } from '@api/useQueryCalls';
import InfoTextComponent from '@atoms/InfoTextComponent';
import { Divider, Text, View } from '@components/primitives';
import { Ionicons } from '@expo/vector-icons';
import PhoneListOrEmptyView from '@molecules/PhoneListOrEmptyView';
import { useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import appStyles from '@root/appStyles';
import { getStore } from '@store/storeUtils';
import colors from '@theme/colors';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SafeAreaView, TouchableOpacity } from 'react-native';
import { onCompleteGetPhoneCallBack, shouldShowLinkToAddPhone } from './enrolledPhonesUtils';
import styles from './styles';

interface PhoneItem {
  status: string;
}

interface RouteParams {
  isTransact?: boolean;
  eventSource?: string;
  transactionCategoryId?: string | number;
}

function shouldShowTickMarkIfSelected(userPhone: string, bpPhone: { mobileNumber: string }) {
  return userPhone === bpPhone.mobileNumber ? (
    <Ionicons name="checkmark" size={18} color={colors.primary.carnation} />
  ) : null;
}

function checkConditionToShowAddLink(
  isTransact: boolean,
  phoneList: PhoneItem[] | null,
  transactionCategoryId?: string | number,
) {
  const rejectedList = phoneList?.filter(item => item.status === 'REJECTED') ?? [];
  if (isTransact && phoneList?.length === 0) return null;
  if (isTransact && phoneList?.length === rejectedList.length) return null;
  return shouldShowLinkToAddPhone(isTransact, transactionCategoryId);
}

function GGEnrolledPhones(): JSX.Element {
  const { t } = useTranslation('common');
  const { loginData, otpData, bpPhone, setBPPhone } = getStore();
  const { mobileNumber } = loginData;
  const { userName } = otpData;
  const [phoneList, setPhoneList] = useState<PhoneItem[] | null>(null);
  const isFocused = useIsFocused();
  const route = useRoute();
  const { isTransact, eventSource, transactionCategoryId } = route.params as RouteParams;

  const { isLoading, isFetching, isSuccess, data, refetch } = fetchAllPhones();
  const navigation = useNavigation();

  useEffect(() => {
    if (isFocused) refetch();
  }, [isFocused]);

  useEffect(() => {
    if (isSuccess && data?.list) {
      onCompleteGetPhoneCallBack(data.list, setPhoneList, !!isTransact, eventSource, transactionCategoryId);
    }
  }, [isSuccess, data]);

  return (
    <SafeAreaView style={appStyles.safeAreaView}>
      <View style={[appStyles.marginHorizontal(16), appStyles.marginVertical(16)]}>
        <Text variant="md-bold" color={colors.neutral.charcoal}>
          {t('phoneTransfer.userPhone')}
        </Text>
        <TouchableOpacity
          onPress={() => {
            if (isTransact) {
              setBPPhone({ mobileNumber, name: userName });
              navigation.pop();
            }
          }}>
          <View style={appStyles.paddingVertical(8)}>
            <View style={styles.mobileNumberWithTick}>
              <Text variant="sm-normal" style={appStyles.flex1}>
                {mobileNumber}
              </Text>
              {shouldShowTickMarkIfSelected(mobileNumber, bpPhone)}
            </View>
            <Text variant="sm-bold" color={colors.neutral.charcoal}>
              {userName}
            </Text>
          </View>
        </TouchableOpacity>
        <Divider color={colors.neutral.mist} height="4px" />
        <View style={appStyles.marginTop(16)}>
          <View style={{ flexDirection: 'row' }}>
            <Text variant="md-bold" style={{ flex: 1 }}>
              {t('addPhone.numberHeader')}
            </Text>
            {checkConditionToShowAddLink(!!isTransact, phoneList, transactionCategoryId)}
          </View>
          <Text variant="xsm-normal" color={colors.neutral.darkMist} style={appStyles.marginTop(4)}>
            {t('phoneTransfer.phoneMaxLimit')}
          </Text>
          {isTransact && phoneList?.some(p => p.status === 'PENDING') ? (
            <InfoTextComponent
              show
              containerStyle={styles.tooltipBannerContainerStyle}
              customTextStyle={styles.tooltipBannerTextStyle}
              variant="xsm-bold"
              iconContainer={styles.tooltipBannerIconContainerStyle}
              iconColor={colors.neutral.charcoal}
              infoText={t('phoneTransfer.phoneTransferWarning')}
            />
          ) : null}
        </View>
      </View>
      <View style={appStyles.flex1}>
        <PhoneListOrEmptyView
          loading={isLoading || isFetching}
          phoneList={phoneList}
          getPhoneNumbers={refetch}
          isTransact={!!isTransact}
          transactionCategoryId={transactionCategoryId}
        />
      </View>
    </SafeAreaView>
  );
}

export default GGEnrolledPhones;
